import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, MapPin, Wrench, Briefcase, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { layoutForPath } from '../utils/layout';
import { extractErrorMessage } from '../utils/apiErrors';
import CustomerFormDialog from '../components/customer/CustomerFormDialog';
import CustomerSitesTab from '../components/customer/CustomerSitesTab';
import CustomerUnitsTab from '../components/customer/CustomerUnitsTab';
import CustomerJobsTab from '../components/customer/CustomerJobsTab';
import { groupSites, normalizedSiteValue } from '../utils/sites';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerProfile() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const Layout = layoutForPath(location.pathname);
  const listPath = location.pathname.replace(/\/[^/]+$/, '');

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customers/${id}/profile`);
      setProfile(res.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to load customer'));
      if (err.response?.status === 404) navigate(listPath, { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, listPath, navigate]);

  useEffect(() => { load(); }, [load]);

  const canManage = ['management', 'foreman', 'field_coordinator', 'field_management'].includes(user?.role)
    || (user?.effective_permissions || []).includes('customers.edit');

  const onCustomerSaved = (saved) => setProfile((p) => ({ ...p, customer: { ...p.customer, ...saved } }));

  if (loading || !profile) {
    return <Layout title="Customer"><div className="p-12 text-center text-slate-500" data-testid="customer-profile-loading">{loading ? 'Loading...' : 'Customer not found'}</div></Layout>;
  }

  const { customer, units, jobs, field_event_count } = profile;
  const stats = [
    { label: 'Sites', value: groupSites(customer.sites).filter((g) => g.name).length, icon: MapPin },
    { label: 'LSDs', value: new Set((customer.sites || []).filter((s) => s.lsd).map((s) => normalizedSiteValue(s.lsd))).size, icon: MapPin },
    { label: 'Customer Assets', value: units.length, icon: Wrench },
    { label: 'Jobs', value: jobs.length, icon: Briefcase },
  ];

  return (
    <Layout title="Customer Profile">
      <div className="space-y-6" data-testid="customer-profile-page">
        <div>
          <button type="button" onClick={() => navigate(listPath)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="customer-profile-back">
            <ArrowLeft size={14} /> All customers
          </button>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-start gap-3 min-w-0">
              <div className="w-12 h-12 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0"><Building2 size={22} /></div>
              <div className="min-w-0">
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 break-normal [overflow-wrap:anywhere]" data-testid="customer-profile-name">{customer.name.split(/(?<=[_-])/).map((part, i) => <span key={i}>{part}<wbr /></span>)}</h3>
                <p className="text-sm text-slate-500">
                  {jobs.length} job{jobs.length === 1 ? '' : 's'} · {field_event_count} field event{field_event_count === 1 ? '' : 's'} · {units.length} asset{units.length === 1 ? '' : 's'}
                </p>
              </div>
            </div>
            {canManage && (
              <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="customer-profile-edit-btn">
                <Pencil size={14} className="mr-2" /> Edit
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3 min-w-0" data-testid={`customer-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <s.icon size={16} className="text-slate-400" />
              <div><p className="text-xl font-bold text-slate-900 leading-none">{s.value}</p><p className="text-xs text-slate-500 mt-1">{s.label}</p></div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="sites" className="space-y-4">
          <TabsList className="grid w-full max-w-lg grid-cols-3 h-auto items-stretch" data-testid="customer-profile-tabs">
            <TabsTrigger className="min-w-0 whitespace-normal" value="sites" data-testid="customer-tab-sites">Sites &amp; LSDs</TabsTrigger>
            <TabsTrigger className="min-w-0 whitespace-normal" value="units" data-testid="customer-tab-units">Customer Assets ({units.length})</TabsTrigger>
            <TabsTrigger className="min-w-0 whitespace-normal" value="jobs" data-testid="customer-tab-jobs">Job History ({jobs.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="sites"><CustomerSitesTab customer={customer} units={units} canManage={canManage} onSaved={onCustomerSaved} onRefresh={load} /></TabsContent>
          <TabsContent value="units"><CustomerUnitsTab units={units} customer={customer} canManage={canManage} onChanged={load} /></TabsContent>
          <TabsContent value="jobs"><CustomerJobsTab jobs={jobs} /></TabsContent>
        </Tabs>

        <CustomerFormDialog open={editOpen} onOpenChange={setEditOpen} customer={customer} onSaved={onCustomerSaved} />
      </div>
    </Layout>
  );
}
